import { Web3Provider } from '@ethersproject/providers'
import { useWeb3React as useWeb3ReactCore } from '@web3-react/core'
import { Web3ReactContextInterface } from '@web3-react/core/dist/types'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import { injected, NETWORK_CHAIN_ID } from '../connectors'
import { NetworkContextName, SupportedChainId, getNativeCurrencySymbol } from '../constants'
import { getActiveChainConfig } from '../constants/chains'

// Get network parameters from centralized config for wallet_addEthereumChain
function getNetworkParams() {
  const config = getActiveChainConfig()
  return {
    chainId: '0x' + config.chainId.toString(16),
    chainName: config.name,
    nativeCurrency: {
      name: config.nativeCurrency.name,
      symbol: config.nativeCurrency.symbol,
      decimals: config.nativeCurrency.decimals
    },
    rpcUrls: [config.rpcUrl],
    blockExplorerUrls: config.blockExplorerUrl ? [config.blockExplorerUrl] : []
  }
}

export function useActiveWeb3React(): Web3ReactContextInterface<Web3Provider> & { chainId?: SupportedChainId } {
  const context = useWeb3ReactCore<Web3Provider>()
  const contextNetwork = useWeb3ReactCore<Web3Provider>(NetworkContextName)
  return context.active ? context : contextNetwork
}

// Get the native currency symbol for the current chain
export function useNativeCurrencySymbol(): string {
  const { chainId } = useActiveWeb3React()
  return getNativeCurrencySymbol(chainId)
}

export function useEagerConnect() {
  const { activate, active } = useWeb3ReactCore() // specifically using useWeb3ReactCore because of what this hook does
  const [tried, setTried] = useState(false)

  useEffect(() => {
    injected.isAuthorized().then(isAuthorized => {
      if (isAuthorized) {
        activate(injected, undefined, true).catch(() => {
          setTried(true)
        })
      } else {
        if (isMobile && window.ethereum) {
          activate(injected, undefined, true).catch(() => {
            setTried(true)
          })
        } else {
          setTried(true)
        }
      }
    })
  }, [activate]) // intentionally only running on mount (make sure it's only mounted once :))

  // if the connection worked, wait until we get confirmation of that to flip the flag
  useEffect(() => {
    if (active) {
      setTried(true)
    }
  }, [active])

  return tried
}

/**
 * Use for network and injected - logs user in
 * and out after checking what network theyre on
 */
export function useInactiveListener(suppress = false) {
  const { active, error, activate } = useWeb3ReactCore() // specifically using useWeb3React because of what this hook does

  useEffect(() => {
    const { ethereum } = window

    if (ethereum && ethereum.on && !active && !error && !suppress) {
      const handleChainChanged = () => {
        // eat errors
        activate(injected, undefined, true).catch(error => {
          console.error('Failed to activate after chain changed', error)
        })
      }

      const handleAccountsChanged = (accounts: string[]) => {
        if (accounts.length > 0) {
          // eat errors
          activate(injected, undefined, true).catch(error => {
            console.error('Failed to activate after accounts changed', error)
          })
        }
      }

      ethereum.on('chainChanged', handleChainChanged)
      ethereum.on('accountsChanged', handleAccountsChanged)

      return () => {
        if (ethereum.removeListener) {
          ethereum.removeListener('chainChanged', handleChainChanged)
          ethereum.removeListener('accountsChanged', handleAccountsChanged)
        }
      }
    }
    return undefined
  }, [active, error, suppress, activate])
}

// Check if the user is on the wrong network
export function useIsWrongNetwork(): boolean {
  const { chainId, active } = useActiveWeb3React()
  // Only check when wallet is connected and chainId is available
  if (!active || !chainId) return false
  return chainId !== NETWORK_CHAIN_ID
}

// Switch to the correct network
export function useSwitchToCorrectNetwork(): () => void {
  const networkParams = useMemo(() => getNetworkParams(), [])

  return useCallback(() => {
    const ethereum = (window as any).ethereum
    if (!ethereum) {
      alert('No ethereum provider found. Please install MetaMask.')
      return
    }

    // Try to switch to the network first
    ethereum
      .request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: networkParams.chainId }]
      })
      .catch((switchError: any) => {
        console.log('Switch error:', switchError)
        // If chain not added (4902) or any other error, try to add the chain
        // Adding a chain will also switch to it automatically
        ethereum
          .request({
            method: 'wallet_addEthereumChain',
            params: [networkParams]
          })
          .catch((addError: any) => {
            console.error('Failed to add network', addError)
            alert('Failed to add network: ' + (addError.message || addError))
          })
      })
  }, [networkParams])
}

// Backward compatibility alias
export const useSwitchToConfluxNetwork = useSwitchToCorrectNetwork
